import React, { useState, useEffect } from "react";
import { db } from "../../../firebase/config";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

const CATEGORIAS = ['Colirios', 'Medicamentos', 'Lentes intraoculares', 'Material quirúrgico', 'Descartables', 'Otros'];

export default function Inventario() {
  const { currentUser, userRole, clinicaId } = useAuth();
  const { theme } = useTheme();
  const [items, setItems] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalMovimiento, setModalMovimiento] = useState(null);
  const [modalHistorial, setModalHistorial] = useState(null);
  const [nuevo, setNuevo] = useState({ nombre: '', categoria: 'Colirios', descripcion: '', stockActual: '', stockMinimo: '5', unidad: 'unidades', proveedor: '', precioReferencia: '', observaciones: '' });
  const [movimiento, setMovimiento] = useState({ tipo: 'entrada', cantidad: '', concepto: '', observaciones: '' });

  const canEdit = ['gestion_general', 'administracion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `0.5px solid ${theme.border}` };

  useEffect(() => {
    if (!clinicaId) return;
    const q = query(collection(db, 'inventario'), where('clinicaId', '==', clinicaId), orderBy('nombre'));
    const unsub = onSnapshot(q, snap => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [clinicaId]);

  useEffect(() => {
    if (!clinicaId) return;
    const q = query(collection(db, 'inventario_movimientos'), where('clinicaId', '==', clinicaId), orderBy('fecha', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setMovimientos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [clinicaId]);

  async function crearItem() {
    if (!nuevo.nombre || !nuevo.stockActual) return;
    await addDoc(collection(db, 'inventario'), {
      ...nuevo, stockActual: parseInt(nuevo.stockActual), stockMinimo: parseInt(nuevo.stockMinimo) || 5,
      precioReferencia: parseFloat(nuevo.precioReferencia) || 0,
      creadoEn: new Date(), creadoPor: currentUser?.email, clinicaId,
    });
    await addDoc(collection(db, 'inventario_movimientos'), {
      itemNombre: nuevo.nombre, categoria: nuevo.categoria, tipo: 'entrada',
      cantidad: parseInt(nuevo.stockActual), concepto: 'Stock inicial',
      observaciones: 'Registro inicial de inventario', fecha: new Date(),
      registradoPor: currentUser?.email, clinicaId,
    });
    await registrarAccion(currentUser?.email, userRole, 'Inventario', 'Crear insumo', nuevo.nombre, clinicaId);
    setModalNuevo(false);
    setNuevo({ nombre: '', categoria: 'Colirios', descripcion: '', stockActual: '', stockMinimo: '5', unidad: 'unidades', proveedor: '', precioReferencia: '', observaciones: '' });
  }

  async function registrarMovimiento() {
    if (!movimiento.cantidad || parseInt(movimiento.cantidad) <= 0) return;
    const item = modalMovimiento;
    const cantidad = parseInt(movimiento.cantidad);
    const nuevoStock = movimiento.tipo === 'entrada' ? item.stockActual + cantidad : Math.max(0, item.stockActual - cantidad);
    await updateDoc(doc(db, 'inventario', item.id), { stockActual: nuevoStock, ultimoMovimiento: new Date() });
    await addDoc(collection(db, 'inventario_movimientos'), {
      itemId: item.id, itemNombre: item.nombre, categoria: item.categoria,
      tipo: movimiento.tipo, cantidad, concepto: movimiento.concepto,
      observaciones: movimiento.observaciones, fecha: new Date(),
      registradoPor: currentUser?.email, stockResultante: nuevoStock, clinicaId,
    });
    await registrarAccion(currentUser?.email, userRole, 'Inventario', `Movimiento ${movimiento.tipo}`, `${item.nombre} · ${cantidad} ${item.unidad}`, clinicaId);
    setModalMovimiento(null);
    setMovimiento({ tipo: 'entrada', cantidad: '', concepto: '', observaciones: '' });
  }

  const itemsConAlerta = items.filter(i => i.stockActual <= (i.stockMinimo || 5) && i.categoria !== 'Lentes intraoculares');

  const historialItem = modalHistorial ? movimientos.filter(m => m.itemId === modalHistorial.id || m.itemNombre === modalHistorial.nombre) : [];

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Inventario" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          {itemsConAlerta.length > 0 && (
            <span style={{ background: theme.errorBg, color: theme.error, fontSize: '12px', padding: '4px 10px', borderRadius: '99px', fontWeight: '500' }}>
              ⚠ {itemsConAlerta.length} alertas de stock
            </span>
          )}
          {canEdit && (
            <button onClick={() => setModalNuevo(true)} style={{ ...btnPrimaryStyle, marginLeft: 'auto' }}>+ Nuevo insumo</button>
          )}
        </div>

        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando...</div>
          : items.length === 0
            ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px', fontSize: '13px' }}>No hay insumos registrados</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {items.map(item => {
                  const alerta = item.stockActual <= (item.stockMinimo || 5) && item.categoria !== 'Lentes intraoculares';
                  return (
                    <div key={item.id} style={{
                      background: theme.bgCard, borderRadius: '10px', padding: '14px 18px',
                      border: `0.5px solid ${alerta ? theme.error : theme.border}`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{item.nombre}</div>
                        <div style={{ fontSize: '12px', color: theme.secondary }}>{item.categoria} · {item.proveedor || '—'}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ textAlign: 'right', marginRight: '8px' }}>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: alerta ? theme.error : theme.success }}>{item.stockActual}</div>
                          <div style={{ fontSize: '11px', color: theme.secondary }}>{item.unidad}</div>
                        </div>
                        <button onClick={() => setModalHistorial(item)} style={{ ...btnSecondaryStyle, fontSize: '12px', padding: '5px 10px' }}>Historial</button>
                        {canEdit && (
                          <button onClick={() => setModalMovimiento(item)} style={{ ...btnPrimaryStyle, fontSize: '12px', padding: '5px 10px' }}>Movimiento</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
        }
      </div>

      {/* MODAL HISTORIAL */}
      {modalHistorial && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Historial — {modalHistorial.nombre}</span>
                <div style={{ fontSize: '12px', color: theme.secondary, marginTop: '2px' }}>Stock actual: <strong style={{ color: theme.accent }}>{modalHistorial.stockActual} {modalHistorial.unidad}</strong></div>
              </div>
              <button onClick={() => setModalHistorial(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            {historialItem.length === 0
              ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '32px', fontSize: '13px' }}>No hay movimientos registrados</div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {historialItem.map(m => {
                    const fecha = m.fecha?.toDate ? m.fecha.toDate().toLocaleDateString('es-AR') : '—';
                    const hora = m.fecha?.toDate ? m.fecha.toDate().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) : '';
                    return (
                      <div key={m.id} style={{ background: theme.bg, borderRadius: '8px', padding: '12px 14px', border: `0.5px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '700', color: m.tipo === 'entrada' ? theme.success : theme.error, textTransform: 'uppercase' }}>
                              {m.tipo === 'entrada' ? '↑ Entrada' : '↓ Salida'}
                            </span>
                            <span style={{ fontSize: '12px', color: theme.secondary }}>{m.concepto || '—'}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: theme.secondary }}>{fecha} {hora} · {m.registradoPor}</div>
                          {m.observaciones && <div style={{ fontSize: '11px', color: theme.secondary, marginTop: '2px', fontStyle: 'italic' }}>{m.observaciones}</div>}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '16px' }}>
                          <div style={{ fontSize: '18px', fontWeight: '700', color: m.tipo === 'entrada' ? theme.success : theme.error }}>
                            {m.tipo === 'entrada' ? '+' : '-'}{m.cantidad}
                          </div>
                          {m.stockResultante !== undefined && (
                            <div style={{ fontSize: '11px', color: theme.secondary }}>Stock: {m.stockResultante}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <button onClick={() => setModalHistorial(null)} style={btnSecondaryStyle}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NUEVO */}
      {modalNuevo && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Nuevo insumo</span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>NOMBRE *</label><input value={nuevo.nombre} onChange={e => setNuevo({...nuevo, nombre: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>CATEGORÍA</label>
                <select value={nuevo.categoria} onChange={e => setNuevo({...nuevo, categoria: e.target.value})} style={inputStyle}>
                  {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>UNIDAD</label><input value={nuevo.unidad} onChange={e => setNuevo({...nuevo, unidad: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>STOCK INICIAL *</label><input type="number" value={nuevo.stockActual} onChange={e => setNuevo({...nuevo, stockActual: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>STOCK MÍNIMO</label><input type="number" value={nuevo.stockMinimo} onChange={e => setNuevo({...nuevo, stockMinimo: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>PROVEEDOR</label><input value={nuevo.proveedor} onChange={e => setNuevo({...nuevo, proveedor: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>PRECIO REFERENCIA</label><input type="number" value={nuevo.precioReferencia} onChange={e => setNuevo({...nuevo, precioReferencia: e.target.value})} style={inputStyle} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>OBSERVACIONES</label><textarea value={nuevo.observaciones} onChange={e => setNuevo({...nuevo, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={crearItem} style={btnPrimaryStyle}>Guardar</button>
              <button onClick={() => setModalNuevo(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MOVIMIENTO */}
      {modalMovimiento && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: '400px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Movimiento — {modalMovimiento.nombre}</span>
              <button onClick={() => setModalMovimiento(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><label style={labelStyle}>TIPO</label>
                <select value={movimiento.tipo} onChange={e => setMovimiento({...movimiento, tipo: e.target.value})} style={inputStyle}>
                  <option value="entrada">Entrada</option>
                  <option value="salida">Salida</option>
                </select>
              </div>
              <div><label style={labelStyle}>CANTIDAD *</label><input type="number" value={movimiento.cantidad} onChange={e => setMovimiento({...movimiento, cantidad: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>CONCEPTO</label><input value={movimiento.concepto} onChange={e => setMovimiento({...movimiento, concepto: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>OBSERVACIONES</label><textarea value={movimiento.observaciones} onChange={e => setMovimiento({...movimiento, observaciones: e.target.value})} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={registrarMovimiento} style={btnPrimaryStyle}>Confirmar</button>
              <button onClick={() => setModalMovimiento(null)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}